import { useState } from "react";
import { useSwipe } from "@/hooks/use-swipe";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { apiRequest } from "@/lib/queryClient";
import type { AmericanoTournament, AmericanoTournamentWithDetails } from "../../../shared/schema";
import { ChevronLeft, ChevronRight, Trophy, CheckCircle, X, Clock, FileEdit } from "lucide-react";

interface LiveAmericanoCardProps {
  tournament: AmericanoTournament;
  currentPlayerId?: string | null;
}

const SCORE_MODE_LABELS: Record<string, string> = {
  points_21: "21 pts", points_13: "13 pts", points_24: "24 pts",
  points_32: "32 pts", classic_sets: "Sets", timed: "Temps",
};

const SCORE_MODE_MULTIPLIER: Record<string, number> = {
  classic_sets: 0.40, points_32: 0.35, points_24: 0.30,
  points_21: 0.28, points_13: 0.22,
};

function getMultiplier(scoreMode: string, timeLimitMinutes?: number | null): number {
  if (scoreMode === "timed") return Math.min(0.15 + (timeLimitMinutes ?? 15) * 0.006, 0.50);
  return SCORE_MODE_MULTIPLIER[scoreMode] ?? 0.28;
}

function estimateEloChanges(
  players: { playerId: string; totalPoints: number }[],
  scoreMode: string,
  timeLimitMinutes?: number | null,
): Map<string, number> {
  const K = 32 * getMultiplier(scoreMode, timeLimitMinutes);
  const n = players.length;
  const pool = n * K;
  if (n === 0) return new Map();
  const avg = players.reduce((s, p) => s + p.totalPoints, 0) / n;
  const deltas = players.map((p) => ({ playerId: p.playerId, delta: p.totalPoints - avg }));
  const sumPositive = deltas.reduce((s, d) => s + Math.max(0, d.delta), 0);
  if (sumPositive === 0) return new Map(players.map((p) => [p.playerId, 0]));
  const changes = new Map<string, number>();
  let totalRounded = 0;
  for (const { playerId, delta } of deltas) {
    const change = Math.round((delta / sumPositive) * pool);
    changes.set(playerId, change);
    totalRounded += change;
  }
  if (totalRounded !== 0) {
    const top = [...players].sort((a, b) => b.totalPoints - a.totalPoints)[0];
    changes.set(top.playerId, (changes.get(top.playerId) ?? 0) - totalRounded);
  }
  return changes;
}

const SCORE_MODE_MAX: Record<string, number> = {
  points_21: 21, points_13: 13, points_24: 24,
  points_32: 32, classic_sets: 3, timed: 21,
};

const getFirstName = (fullName: string) => {
  const parts = fullName.split(" ");
  return parts.length > 1 ? parts[parts.length - 1] : parts[0];
};

function useDuration(startedAt: Date | string | null) {
  const [, rerender] = useState(0);
  if (!startedAt) return "0:00";
  const start = new Date(startedAt);
  const diffS = Math.floor((Date.now() - start.getTime()) / 1000);
  const h = Math.floor(diffS / 3600);
  const m = Math.floor((diffS % 3600) / 60);
  const s = diffS % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${m}:${String(s).padStart(2, "0")}`;
}

type TeamChoice = { type: "win" } | { type: "lost"; score: number };
type MatchPending = { a?: TeamChoice; b?: TeamChoice };
type LostPicker = { matchId: string; team: "a" | "b" } | null;

export default function LiveAmericanoCard({ tournament, currentPlayerId }: LiveAmericanoCardProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [pageIndex, setPageIndex] = useState(0);
  const swipeHandlers = useSwipe(
    () => setPageIndex((i) => i === rounds.length - 1 ? -1 : Math.min(rounds.length - 1, i + 1)),
    () => setPageIndex((i) => i === -1 ? rounds.length - 1 : i - 1),
  );
  const [pending, setPending] = useState<Record<string, MatchPending>>({});
  const [lostPicker, setLostPicker] = useState<LostPicker>(null);
  const duration = useDuration(tournament.startedAt);

  const maxScore = tournament.scoreMode === "timed"
    ? (tournament.timeLimitMinutes ?? 15)
    : (SCORE_MODE_MAX[tournament.scoreMode] ?? 21);

  const { data: details } = useQuery<AmericanoTournamentWithDetails>({
    queryKey: ["/api/americano", tournament.id],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/americano/${tournament.id}`);
      return res.json();
    },
    refetchInterval: 10000,
  });

  const scoreMutation = useMutation({
    mutationFn: ({ matchId, scoreTeamA, scoreTeamB }: { matchId: string; scoreTeamA: number; scoreTeamB: number }) =>
      apiRequest("PATCH", `/api/americano/${tournament.id}/matches/${matchId}/score`, { scoreTeamA, scoreTeamB }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/americano", tournament.id] }),
    onError: () => toast({ title: t("americano.create.errorScore"), variant: "destructive" }),
  });

  const cancelMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/americano/${tournament.id}/cancel`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/americano", tournament.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/americano/active"] });
      toast({ title: t("americano.status.cancelled") });
    },
    onError: () => toast({ title: t("common.error"), variant: "destructive" }),
  });

  const finalizeMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/americano/${tournament.id}/complete`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/americano", tournament.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/americano/active"] });
      toast({ title: t("americano.card.finalize"), description: t("matches.eloUpdated", { defaultValue: "ELO updated." }) });
    },
    onError: () => toast({ title: t("americano.create.errorFinalize"), variant: "destructive" }),
  });

  const saveChoices = (matchId: string, a: TeamChoice, b: TeamChoice) => {
    let scoreTeamA: number;
    let scoreTeamB: number;
    if (tournament.scoreMode === "classic_sets") {
      const lostScore = a.type === "lost" ? a.score : b.type === "lost" ? b.score : 0;
      const winScore = lostScore >= 5 ? 7 : 6;
      scoreTeamA = a.type === "win" ? winScore : a.score;
      scoreTeamB = b.type === "win" ? winScore : b.score;
    } else {
      scoreTeamA = a.type === "win" ? maxScore : a.score;
      scoreTeamB = b.type === "win" ? maxScore : b.score;
    }
    scoreMutation.mutate({ matchId, scoreTeamA, scoreTeamB });
    setPending((p) => { const next = { ...p }; delete next[matchId]; return next; });
  };

  const handleWin = (matchId: string, team: "a" | "b") => {
    const choice: TeamChoice = { type: "win" };
    const other = team === "a" ? "b" : "a";
    const otherChoice = (pending[matchId] ?? {})[other];
    if (otherChoice) {
      // Other team already chose → save now
      const choices = { [team]: choice, [other]: otherChoice } as { a: TeamChoice; b: TeamChoice };
      saveChoices(matchId, choices.a, choices.b);
    } else {
      // Mark this team as WIN, auto-open LOST picker for the other team
      setPending((p) => ({ ...p, [matchId]: { ...p[matchId], [team]: choice } }));
      setLostPicker({ matchId, team: other });
    }
  };

  const handleLost = (matchId: string, team: "a" | "b") => {
    setLostPicker({ matchId, team });
  };

  const handleLostPick = (score: number) => {
    if (!lostPicker) return;
    const { matchId, team } = lostPicker;
    const choice: TeamChoice = { type: "lost", score };
    const other = team === "a" ? "b" : "a";
    const otherChoice = (pending[matchId] ?? {})[other];
    setLostPicker(null);
    if (otherChoice) {
      const choices = { [team]: choice, [other]: otherChoice } as { a: TeamChoice; b: TeamChoice };
      saveChoices(matchId, choices.a, choices.b);
    } else {
      setPending((p) => ({ ...p, [matchId]: { ...p[matchId], [team]: choice } }));
    }
  };

  const handleReset = (matchId: string) => {
    scoreMutation.mutate({ matchId, scoreTeamA: 0, scoreTeamB: 0 });
  };

  const rounds = details?.rounds ?? [];
  const isCreator = details?.createdByPlayerId === currentPlayerId;
  const canInteract = isCreator || details?.players.some((p) => p.playerId === currentPlayerId);
  const canScore = tournament.status === "in_progress";

  const currentRound = pageIndex >= 0 ? rounds[pageIndex] : null;
  const standings = details ? [...details.players].sort((a, b) => b.totalPoints - a.totalPoints) : [];
  const allMatchesDone = rounds.length > 0 && rounds.every((r) => r.matches.every((m) => m.isCompleted));

  const totalMatches = rounds.reduce((s, r) => s + r.matches.filter((m) => m.isCompleted).length, 0);

  const estimatedElo = allMatchesDone && standings.some((ap) => ap.eloChange == null)
    ? estimateEloChanges(
        standings.map((ap) => ({ playerId: ap.playerId, totalPoints: ap.totalPoints })),
        tournament.scoreMode,
        tournament.timeLimitMinutes,
      )
    : null;
  const roundAllScored = (r: typeof rounds[number]) => r.matches.every((m) => m.isCompleted);

  const creatorPlayer = details?.createdBy;
  const creatorName = creatorPlayer ? getFirstName(creatorPlayer.name) : null;

  return (
    <>
      <Card className="bg-white dark:bg-card border border-gray-200 dark:border-border overflow-hidden" {...swipeHandlers}>

        {/* ── Top band ── */}
        <div className="bg-gray-50 dark:bg-muted px-4 py-2 border-b dark:border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-2 h-2 bg-red-500 rounded-full shrink-0" />
              <span className="text-xs font-medium text-gray-600 dark:text-muted-foreground shrink-0">{t("americano.card.live")}</span>
              {isCreator && (
                <button
                  onClick={() => cancelMutation.mutate()}
                  disabled={cancelMutation.isPending}
                  className="text-red-500 hover:text-red-700 transition-colors disabled:opacity-40"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-2">
              <span className="text-xs font-semibold text-gray-800 dark:text-foreground truncate max-w-[120px]">{tournament.name}</span>
              {rounds.length > 0 && pageIndex >= 0 && (
                <span className="text-xs text-gray-500 dark:text-muted-foreground shrink-0">
                  Round {currentRound?.roundNumber ?? 0}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── Navigation dots ── */}
        <div className="flex items-center justify-between px-2 py-1.5">
          <button
            onClick={() => setPageIndex((i) => i === -1 ? rounds.length - 1 : i - 1)}
            disabled={rounds.length === 0}
            className="p-1 rounded disabled:opacity-20 hover:bg-gray-100 dark:hover:bg-muted"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-1">
            <button onClick={() => setPageIndex(-1)}
              className={`h-1.5 rounded-full transition-all ${pageIndex === -1 ? "w-6 bg-[#001489]" : "w-1.5 bg-gray-300 dark:bg-gray-600"}`}
            />
            {rounds.map((r, idx) => (
              <button key={r.id} onClick={() => setPageIndex(idx)}
                className={`h-1.5 rounded-full transition-all ${
                  idx === pageIndex ? "w-6 bg-[#001489]"
                  : roundAllScored(r) ? "w-1.5 bg-padel-green/60"
                  : "w-1.5 bg-gray-200 dark:bg-gray-700"
                }`}
              />
            ))}
          </div>
          <button
            onClick={() => setPageIndex((i) => i === rounds.length - 1 ? -1 : i + 1)}
            disabled={rounds.length === 0}
            className="p-1 rounded disabled:opacity-20 hover:bg-gray-100 dark:hover:bg-muted"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* ── Round complete checkmark ── */}
        {pageIndex >= 0 && currentRound && roundAllScored(currentRound) && (
          <div className="text-center pb-1">
            <CheckCircle className="inline h-3 w-3 text-padel-green" />
          </div>
        )}

        {/* ── Classement ── */}
        {pageIndex === -1 && (
          <div className="px-3 pb-3 space-y-1.5">
            {standings.length === 0 ? (
              <div className="text-center py-3 text-xs text-muted-foreground">{t("americano.card.noScore")}</div>
            ) : standings.map((ap, idx) => {
              const isMe = ap.playerId === currentPlayerId;
              return (
                <div key={ap.id} className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 border ${isMe ? "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800" : "bg-white dark:bg-card border-gray-100 dark:border-border"}`}>
                  <span className="w-4 text-center text-xs text-muted-foreground font-medium">
                    {idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : idx + 1}
                  </span>
                  <span className="flex-1 text-xs font-medium truncate">{ap.player?.name}</span>
                  {(() => {
                    const elo = ap.eloChange ?? estimatedElo?.get(ap.playerId);
                    if (elo == null) return null;
                    return (
                      <span className={`text-xs font-semibold w-14 text-right ${elo >= 0 ? "text-blue-800" : "text-red-600"}`}>
                        {elo >= 0 ? "+" : ""}{elo} ELO
                      </span>
                    );
                  })()}
                  <span className="text-xs font-bold w-14 text-right">{ap.totalPoints} pts</span>
                </div>
              );
            })}
            {canInteract && canScore && allMatchesDone && (
              <Button className="w-full h-8 text-xs mt-2 bg-[#001489] hover:bg-[#001489]/90 text-white"
                onClick={() => finalizeMutation.mutate()}
                disabled={finalizeMutation.isPending}
              >
                <Trophy className="h-3.5 w-3.5 mr-1.5" />
                {t("americano.card.finalize")}
              </Button>
            )}
          </div>
        )}

        {/* ── Round matches ── */}
        {pageIndex >= 0 && currentRound && (
          <div className="px-3 pb-3 space-y-2">
            {!details ? (
              <div className="text-center py-4 text-xs text-muted-foreground">{t("americano.card.loading")}</div>
            ) : currentRound.matches.map((match) => {
              const isCompleted = match.isCompleted;
              const scoreA = match.scoreTeamA;
              const scoreB = match.scoreTeamB;
              const teamA = `${getFirstName(match.teamALeftPlayer?.name ?? "")} / ${getFirstName(match.teamARightPlayer?.name ?? "")}`;
              const teamB = `${getFirstName(match.teamBLeftPlayer?.name ?? "")} / ${getFirstName(match.teamBRightPlayer?.name ?? "")}`;
              const canEdit = !!(canScore && canInteract);
              const matchPending = pending[match.id] ?? {};

              const renderTeamRight = (team: "a" | "b", score: number, oppScore: number) => {
                const choice = matchPending[team];
                if (canEdit && !isCompleted) {
                  if (choice) {
                    // Already chosen, waiting for other team
                    const label = choice.type === "win"
                    ? (tournament.scoreMode === "classic_sets" ? "WIN" : `WIN · ${maxScore}`)
                    : `LOST · ${choice.score}`;
                    const color = choice.type === "win" ? "text-padel-green dark:text-green-400 bg-padel-green/20 dark:bg-padel-green/30" : "text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/30";
                    return (
                      <span className={`text-xs font-semibold px-2.5 h-7 flex items-center rounded-full ${color}`}>
                        {label}
                      </span>
                    );
                  }
                  return (
                    <div className="flex items-center gap-1.5 shrink-0 ml-2">
                      <button
                        onPointerDown={(e) => { e.preventDefault(); handleWin(match.id, team); }}
                        className="px-3 h-7 rounded-full bg-padel-green/20 dark:bg-padel-green/30 text-padel-green dark:text-green-400 text-xs font-semibold active:bg-padel-green/40 touch-manipulation"
                      >WIN</button>
                      <button
                        onPointerDown={(e) => { e.preventDefault(); handleLost(match.id, team); }}
                        className="px-3 h-7 rounded-full bg-red-50 dark:bg-red-900/30 text-red-500 dark:text-red-400 text-xs font-semibold active:bg-red-100 touch-manipulation"
                      >LOST</button>
                    </div>
                  );
                }
                return (
                  <div className="flex items-center gap-1.5 ml-2">
                    {canEdit ? (
                      <button
                        onPointerDown={(e) => { e.preventDefault(); handleReset(match.id); }}
                        className={`font-bold text-sm w-7 text-center underline decoration-dotted touch-manipulation ${score > oppScore ? "text-blue-800" : score < oppScore ? "text-red-600" : "text-muted-foreground"}`}
                      >{score}</button>
                    ) : (
                      <span className={`font-bold text-sm w-7 text-center ${score > oppScore ? "text-blue-800" : score < oppScore ? "text-red-600" : "text-muted-foreground"}`}>
                        {score}
                      </span>
                    )}
                  </div>
                );
              };

              return (
                <div key={match.id} className="rounded-xl overflow-hidden border border-gray-200 dark:border-border">
                  <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100 dark:border-border bg-white dark:bg-card">
                    <span className="font-medium text-sm truncate flex-1">{teamA}</span>
                    {renderTeamRight("a", scoreA, scoreB)}
                  </div>
                  <div className="flex items-center justify-between px-3 py-2.5 bg-white dark:bg-card">
                    <span className="font-medium text-sm truncate flex-1">{teamB}</span>
                    {renderTeamRight("b", scoreB, scoreA)}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Bottom band ── */}
        <div className="bg-gray-50 dark:bg-muted px-4 py-2 border-t dark:border-border">
          <div className="flex justify-between items-center text-xs text-gray-600 dark:text-muted-foreground">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3 text-gray-500" />
                {duration}
              </span>
              {creatorName && (
                <span className="flex items-center gap-1">
                  <FileEdit className="w-3 h-3 text-gray-500" />
                  {creatorName}
                </span>
              )}
            </div>
            <span className="text-gray-500">
              {SCORE_MODE_LABELS[tournament.scoreMode] ?? t("americano.scoreMode.timed")} · {t("americano.view.court", { count: tournament.numberOfCourts })}
            </span>
          </div>
        </div>

      </Card>

      {/* ── LOST score picker overlay ── */}
      {lostPicker && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onPointerDown={() => setLostPicker(null)}
        >
          <div
            className="bg-white dark:bg-card rounded-2xl w-full max-w-sm mx-4 p-4 pb-6 shadow-xl"
            onPointerDown={(e) => e.stopPropagation()}
          >
<div className="text-center text-sm font-semibold mb-4 text-gray-700 dark:text-foreground">
              {t("americano.card.lostPickerTitle", { max: tournament.scoreMode === "classic_sets" ? 6 : maxScore - 1 })}
            </div>
            <div className="grid grid-cols-6 gap-2">
              {(tournament.scoreMode === "classic_sets"
                ? Array.from({ length: 7 }, (_, i) => i)
                : Array.from({ length: maxScore }, (_, i) => i)
              ).map((n) => (
                <button
                  key={n}
                  onPointerDown={(e) => { e.preventDefault(); handleLostPick(n); }}
                  className="h-11 rounded-xl bg-gray-100 dark:bg-muted font-bold text-sm hover:bg-gray-200 active:bg-[#001489] active:text-white touch-manipulation transition-colors"
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
